import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MapPin, Briefcase, Calendar, MessageSquare, Edit2, Save, Phone } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ConnectButton from '../components/ConnectButton';

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});

  const isOwnProfile = !userId || userId === currentUser?.id;

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      if (isOwnProfile) {
        setProfile(currentUser);
        setFormData(currentUser);
      } else {
        const { data } = await api.get(`/users/profile/${userId}`);
        setProfile(data);
      }
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await api.post('/users/upload-photo', { photo: reader.result });
          const updatedUser = { ...currentUser, profile_photo: reader.result };
          updateUser(updatedUser);
          setProfile(updatedUser);
          toast.success('Photo updated');
        } catch (error) {
          toast.error('Failed to upload photo');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put('/users/update', formData);
      const { data } = await api.get('/auth/me');
      updateUser(data);
      setProfile(data);
      setEditing(false);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-12">
          <p className="text-slate-500">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="hidden lg:block lg:col-span-3 sticky top-24 h-fit">
            <Sidebar />
          </div>

          <div className="col-span-12 lg:col-span-9" data-testid="profile-container">
            <Card className="bg-white border border-slate-100 shadow-sm p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <Avatar className="h-24 w-24">
                      <AvatarImage src={profile?.profile_photo} />
                      <AvatarFallback className="bg-[#1E3A8A] text-white text-3xl">
                        {getInitials(profile?.name)}
                      </AvatarFallback>
                    </Avatar>
                    {isOwnProfile && (
                      <div className="absolute bottom-0 right-0">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label htmlFor="photo-upload">
                          <Button
                            size="icon"
                            className="h-8 w-8 rounded-full bg-[#22C55E] hover:bg-[#16A34A]"
                            asChild
                          >
                            <span>
                              <Edit2 className="h-4 w-4" />
                            </span>
                          </Button>
                        </label>
                      </div>
                    )}
                  </div>
                  <div>
                    <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase">
                      {profile?.name}
                    </h1>
                    <p className="text-sm text-slate-600 mt-2">GBA Alumni</p>
                  </div>
                </div>

                {isOwnProfile ? (
                  <Button
                    onClick={() => (editing ? handleSaveProfile() : setEditing(true))}
                    className="bg-[#1E3A8A] hover:bg-[#172554]"
                    data-testid="edit-profile-button"
                  >
                    {editing ? (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    ) : (
                      <>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Profile
                      </>
                    )}
                  </Button>
                ) : (
                  <ConnectButton userId={profile?.id || userId} size="default" />
                )}
              </div>

              {/* Profile Info */}
              <div className="grid md:grid-cols-2 gap-6">
                {editing ? (
                  <>
                    <div>
                      <Label>Current Role</Label>
                      <Input
                        value={formData.current_role || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, current_role: e.target.value })
                        }
                        placeholder="Your current role"
                      />
                    </div>
                    <div>
                      <Label>Industry</Label>
                      <Input
                        value={formData.industry || ''}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        placeholder="Your industry"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={formData.city || ''}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Your city"
                      />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <Input
                        value={formData.phone || ''}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        data-testid="profile-phone-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Required — visible only to admins and connections</p>
                    </div>
                    <div>
                      <Label>Batch</Label>
                      <Input
                        value={formData.batch || ''}
                        onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                        placeholder="Your batch"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Bio</Label>
                      <Textarea
                        value={formData.bio || ''}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        placeholder="Tell us about yourself"
                        rows={3}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>GBA Journey</Label>
                      <Textarea
                        value={formData.gba_journey || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, gba_journey: e.target.value })
                        }
                        placeholder="Your journey with GBA"
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {profile?.current_role && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <Briefcase className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">
                            Current Role
                          </p>
                          <p className="font-medium">{profile.current_role}</p>
                        </div>
                      </div>
                    )}

                    {profile?.city && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <MapPin className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Location</p>
                          <p className="font-medium">{profile.city}</p>
                        </div>
                      </div>
                    )}

                    {profile?.batch && (
                      <div className="flex items-center gap-3 text-slate-700">
                        <Calendar className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Batch</p>
                          <p className="font-medium">
                            {profile.batch} {profile.year ? `- ${profile.year}` : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {profile?.phone && isOwnProfile && (
                      <div className="flex items-center gap-3 text-slate-700" data-testid="profile-phone-display">
                        <Phone className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Phone</p>
                          <p className="font-medium">{profile.phone}</p>
                        </div>
                      </div>
                    )}

                    {profile?.bio && (
                      <div className="md:col-span-2">
                        <h3 className="font-semibold text-slate-900 mb-2">About</h3>
                        <p className="text-slate-700">{profile.bio}</p>
                      </div>
                    )}

                    {profile?.gba_journey && (
                      <div className="md:col-span-2">
                        <h3 className="font-semibold text-slate-900 mb-2">GBA Journey</h3>
                        <p className="text-slate-700">{profile.gba_journey}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
